import React, { PropsWithChildren, useCallback, useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, ButtonModel, IconRegularInfo, Spinner } from '@notino/react-styleguide'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import axios, { AxiosError } from 'axios'

// hooks
import useMessages from '../../../hooks/useMessages'
import useLocalStorage from '../../../hooks/useLocalStorage'

// styles
import * as SC from './Step3BookingConfirmationStyles'

// components
import ReservationBookingModalLayout from '../ReservationBookingModalLayout/ReservationBookingModalLayout'
import HookFormField from '../../../formFields/HookFormField'
import InputFormField from '../../../formFields/InputFormField/InputFormField'
import PhoneFormField from '../../../formFields/PhoneFormField/PhoneFormField'
import CheckboxFormField from '../../../formFields/CheckboxFormField/CheckboxFormField'
import InfoLine from '../../../atoms/InfoLine/InfoLine'
import Tooltip from '../../Tooltip/Tooltip'
import CartReservationCountdown from '../CartReservationCountdown/CartReservationCountdown'
import ReservationBookingDialog from '../ReservationBookingDialog/ReservationBookingDialog'
import useCartReservationProgress from '../CartReservationCountdown/useCartReservationProgress'
import VisuallyHidden from '../../../atoms/VisuallyHidden/VisuallyHidden'

// types
import {
	BasicAxiosError,
	ConfigResponse,
	DocumentsResponse,
	PostCartSubmitBody,
	PostCartSubmitResponse,
	PostCartSubmitSuccessResponse,
	ReservationBookingConfirmationForm,
	ReservationBookingSelectedFormValues,
	SalonServiceResponse,
	ServiceCategoryParameterValueData
} from '../../../types/types'

// utils
import {
	APP_CONTAINER_ID,
	DEFAULT_DATE_FORMAT,
	DOCUMENTS,
	FORM,
	LOCALE_STORAGE_KEYS,
	MSG_TYPE,
	RESERVATION_BOOKING_MODAL_STEP,
	SERVICE_TYPE,
	VALIDATION_MAX_LENGTH
} from '../../../utils/enums'
import { INFO_LINE_TYPE } from '../../../atoms/InfoLine/types'
import { AppContext } from '../../../utils/appProvider'
import { POST_CART_SUBMIT_ERROR_KEYS_MAP } from '../../../utils/clientErrorValidations'
import { RESERVATION_BOOKING_MODAL_Z_INDEXES, getUnavailableServicesModalDescription } from '../reservationBookingModalHelpers'
import { getCheckboxClickEvent, getFormSubmitFailedEvent, getModalReservationConfirmEvent } from '../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../utils/dataLayer'
import { ERROR_MESSAGES, getLinkAttributes, showNotifications } from '../../../utils/helper'
import type { ReservationDeviceStorage } from '../../../utils/reservationDeviceId'

export type PostCartSubmitReservation = PostCartSubmitSuccessResponse['reservations'][number]

type PostCartSubmitValidationResponse = Extract<PostCartSubmitResponse, { validationResult: unknown }>

const isPostCartSubmitSuccessResponse = (response: PostCartSubmitResponse): response is PostCartSubmitSuccessResponse => 'reservations' in response

const isPostCartSubmitValidationResponse = (response: PostCartSubmitResponse): response is PostCartSubmitValidationResponse => 'validationResult' in response
type Props = PropsWithChildren<{
	configData: ConfigResponse
	selectedFormValues: ReservationBookingSelectedFormValues
	selectedIndustryID: string | undefined
	selectedServiceData: SalonServiceResponse['service']
	selectedServiceCategoryParameterValueData: ServiceCategoryParameterValueData | undefined
	headerImage: string | undefined
	onClose: () => void
	onBackButtonClick: () => void
	onUnavailableServicesAcknowledged: () => void
	onSubmitBookingConfirmationForm: (submittedValues?: PostCartSubmitSuccessResponse) => void
	onCartExpired: () => void
	documentsData: DocumentsResponse['documents'] | undefined
	onLogin: () => void
	onRegister: () => void
}>

type AgreementLinkProps = PropsWithChildren<{ link: string }>

const AgreementLink = (props: AgreementLinkProps) => {
	const { children, link } = props
	const { productionEnvironment } = useContext(AppContext)
	const { locale } = useIntl()

	return (
		<SC.AgreementLink href={link} {...getLinkAttributes(link, productionEnvironment, locale)} onClick={(e) => e.stopPropagation()}>
			{children}
		</SC.AgreementLink>
	)
}

const Step3BookingConfirmation = (props: Props) => {
	const {
		onClose,
		onBackButtonClick,
		onUnavailableServicesAcknowledged,
		onSubmitBookingConfirmationForm,
		onCartExpired,
		selectedFormValues,
		selectedIndustryID,
		selectedServiceData,
		selectedServiceCategoryParameterValueData,
		configData,
		documentsData,
		headerImage,
		onLogin,
		onRegister
	} = props

	const { messages } = useMessages()
	const { formatMessage } = useIntl()
	const { apiBrowser, isAuthorized } = useContext(AppContext)
	const { storedValue: cartReservationDeviceData } = useLocalStorage<ReservationDeviceStorage>(LOCALE_STORAGE_KEYS.DEVICE_ID)
	const [isCartExpiredModalOpen, setIsCartExpiredModalOpen] = useState(false)
	const [isUnavailableServicesModalOpen, setIsUnavailableServicesModalOpen] = useState(false)
	const [unavailableServicesModalDescription, setUnavailableServicesModalDescription] = useState('')
	const { cartData, expiresIn, progressState } = useCartReservationProgress({ hideProgressOnError: true })
	const cartExpirationSeconds = configData.cartExpiration
	const reservationItems = cartData?.cart.cartReservationItems ?? []

	const reservationTermsLink = documentsData?.find((document) => document.assetType === DOCUMENTS.B2C_RESERVATIONS_TERMS)?.files[0]?.original
	const privacyPolicyLink = documentsData?.find((document) => document.assetType === DOCUMENTS.B2C_PRIVACY_POLICY)?.files[0]?.original
	const isGroupReservation = selectedServiceData.type === SERVICE_TYPE.CUSTOM_GROUP

	const {
		date,
		timeSlot,
		employeeID: formValuesEmployeeID
	} = isGroupReservation
		? selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]
		: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]

	const employeeID = formValuesEmployeeID || timeSlot.employeeID
	const bookingConfirmationFormValues = selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION]

	const {
		control,
		handleSubmit,
		watch,
		setError,
		formState: { isSubmitting }
	} = useForm<ReservationBookingConfirmationForm>({
		defaultValues: {
			firstName: bookingConfirmationFormValues.firstName,
			lastName: bookingConfirmationFormValues.lastName,
			email: bookingConfirmationFormValues.email,
			phone: bookingConfirmationFormValues.phone,
			agreement: bookingConfirmationFormValues.agreement,
			isVisitor: bookingConfirmationFormValues.isVisitor,
			notAllowMarketing: bookingConfirmationFormValues.notAllowMarketing,
			note: bookingConfirmationFormValues.note,
			visitor: {
				firstName: bookingConfirmationFormValues.visitor?.firstName,
				lastName: bookingConfirmationFormValues.visitor?.lastName
			}
		},
		values: {
			firstName: bookingConfirmationFormValues.firstName,
			lastName: bookingConfirmationFormValues.lastName,
			email: bookingConfirmationFormValues.email,
			phone: bookingConfirmationFormValues.phone,
			agreement: bookingConfirmationFormValues.agreement,
			isVisitor: bookingConfirmationFormValues.isVisitor,
			notAllowMarketing: bookingConfirmationFormValues.notAllowMarketing,
			note: bookingConfirmationFormValues.note,
			visitor: bookingConfirmationFormValues.visitor
				? {
						firstName: bookingConfirmationFormValues.visitor.firstName,
						lastName: bookingConfirmationFormValues.visitor.lastName
					}
				: undefined
		}
	})

	const { firstName, lastName, email, phone, agreement, isVisitor, visitor } = watch()
	const { firstName: visitorFirstName, lastName: visitorLastName } = visitor || {}
	const { phoneNumber, phonePrefixCountryCode } = phone

	const isVisitorFilledIn = !isVisitor || !!(visitorFirstName && visitorLastName)
	const disabledSubmitButton =
		isSubmitting || !firstName || !lastName || !email || !phonePrefixCountryCode || !phoneNumber || !agreement || !isVisitorFilledIn

	// NOTE: Button from Notino style-guide library doesn't recognize "form" as a prop, however, when passed to the component, it works fine
	const unknownButtonProps = {
		form: FORM.RESERVATION_BOOKING_MODAL_BOOKING_CONFIRMATION
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any

	const handleCartExpired = useCallback(() => {
		setIsCartExpiredModalOpen(true)
	}, [])

	const handleCartExpiredConfirm = () => {
		setIsCartExpiredModalOpen(false)
		onCartExpired()
	}

	const handleUnavailableServicesModalConfirm = () => {
		setIsUnavailableServicesModalOpen(false)
		setUnavailableServicesModalDescription('')
		onUnavailableServicesAcknowledged()
		onBackButtonClick()
	}

	const getVisitorData = (visitorData: ReservationBookingConfirmationForm['visitor']) => {
		if (!visitorData) return undefined

		if (!visitorData?.firstName || !visitorData?.lastName) {
			if (!visitorData?.firstName) {
				setError('visitor.firstName', { message: messages['This field is required'] })
			}
			if (!visitorData?.lastName) {
				setError('visitor.lastName', { message: messages['This field is required'] })
			}
			return null
		}

		return {
			firstName: visitorData.firstName,
			lastName: visitorData.lastName
		}
	}

	const handleSubmitBookingConfirmationForm = async (values: ReservationBookingConfirmationForm) => {
		const deviceID = cartReservationDeviceData?.deviceID

		if (!deviceID) {
			showNotifications([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
			return
		}

		try {
			if (values.isVisitor && !getVisitorData(values.visitor)) {
				return
			}

			const visitorData = values.isVisitor ? getVisitorData(values.visitor) : undefined

			const requestBody: PostCartSubmitBody = {
				allowMarketing: !values.notAllowMarketing,
				customer: {
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					phonePrefixCountryCode: values.phone.phonePrefixCountryCode,
					phone: values.phone.phoneNumber.replace(/\s+/g, '')
				},
				...(visitorData ? { visitor: visitorData } : {})
			}

			const response = await apiBrowser.b2c.postCartSubmit(deviceID, requestBody, {
				formErrors: { setError, errorsMap: POST_CART_SUBMIT_ERROR_KEYS_MAP }
			})

			if (isPostCartSubmitValidationResponse(response)) {
				const unavailableItemIDs = response.validationResult?.cartReservationItems?.filter((item) => !item.isAvailable).map((item) => item.id) || []

				if (unavailableItemIDs.length > 0) {
					setUnavailableServicesModalDescription(
						getUnavailableServicesModalDescription({
							unavailableItemIDs,
							reservationItems,
							formatMessage,
							fallbackServiceName: messages.Service
						})
					)
					setIsUnavailableServicesModalOpen(true)
					return
				}
			}

			if (!isPostCartSubmitSuccessResponse(response)) {
				return
			}
			onSubmitBookingConfirmationForm(response)
		} catch (error) {
			if (axios.isAxiosError(error)) {
				// Send the first error to Google Analytics for tracking form submission failures
				const axiosError = error as AxiosError<BasicAxiosError>
				const firstMessage = axiosError.response?.data?.messages?.[0]

				const formErrorAnalyticsData = {
					type: 'reservation_user_details',
					error_message: firstMessage?.message || error.message,
					name: firstMessage?.path,
					interaction: 'button' as const
				}

				pushToDataLayer(getFormSubmitFailedEvent(formErrorAnalyticsData))
			}
		}
	}

	return (
		<ReservationBookingModalLayout
			title={messages['Personal information']}
			headerImage={headerImage}
			onBackButtonClick={onBackButtonClick}
			onClose={onClose}
			footerContent={
				// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
				<div
					onClick={() => {
						// Push analytics event to dataLayer on every click, even when the button is disabled
						const event = getModalReservationConfirmEvent({
							category: selectedIndustryID,
							subcategory: selectedServiceData.category.child?.id,
							type: selectedServiceData.category.child.child?.id,
							subtype: selectedServiceCategoryParameterValueData?.categoryParameterValueID,
							employee_id: employeeID,
							date: dayjs(date).format(DEFAULT_DATE_FORMAT),
							slot: `${timeSlot.timeFrom} - ${timeSlot.timeTo}`
						})
						pushToDataLayer(event)
					}}
				>
					<Button
						{...unknownButtonProps}
						type={'submit'}
						buttonStyle={ButtonModel.Styles.primary}
						fullWidth
						disabled={disabledSubmitButton}
						aria-busy={isSubmitting}
					>
						{isSubmitting ? (
							<>
								<Spinner size={16} color={'icon.disabled'} aria-hidden />
								<VisuallyHidden>{messages['Make a reservation']}</VisuallyHidden>
							</>
						) : (
							messages['Make a reservation']
						)}
					</Button>
				</div>
			}
		>
			<CartReservationCountdown
				cartExpirationSeconds={cartExpirationSeconds}
				expiresInSeconds={expiresIn}
				progressState={progressState}
				onExpired={handleCartExpired}
				title={messages['Personal information']}
				description={messages['Please fill in the personal details required for the reservation.']}
				edgeOffset
			/>
			<SC.Form id={FORM.RESERVATION_BOOKING_MODAL_BOOKING_CONFIRMATION} onSubmit={handleSubmit(handleSubmitBookingConfirmationForm)} noValidate>
				<SC.FormSectionWrapper>
					<HookFormField
						control={control}
						name={'firstName'}
						component={InputFormField}
						label={messages.Name}
						placeholder={messages['Your name']}
						maxLength={VALIDATION_MAX_LENGTH.LENGTH_255}
						required
					/>
					<HookFormField
						control={control}
						name={'lastName'}
						component={InputFormField}
						label={messages.Surname}
						placeholder={messages['Your surname']}
						maxLength={VALIDATION_MAX_LENGTH.LENGTH_255}
						required
					/>
					<HookFormField
						control={control}
						name={'email'}
						type={'email'}
						component={InputFormField}
						label={messages.Email}
						placeholder={messages['Your email']}
						maxLength={VALIDATION_MAX_LENGTH.LENGTH_255}
						required
						disabled={isAuthorized && !!bookingConfirmationFormValues.email}
					/>
					{!isAuthorized && (
						<InfoLine
							type={INFO_LINE_TYPE.INFO}
							message={
								<FormattedMessage
									id='To manage your reservations, you need to log in first. <login>Log in</login> or <createAccount>Create an account</createAccount>.'
									defaultMessage='To manage your reservations, you need to log in first. <login>Log in</login> or <createAccount>Create an account</createAccount>.'
									values={{
										login: (chunks) => <SC.AuthLink onClick={onLogin}>{chunks}</SC.AuthLink>,
										createAccount: (chunks) => <SC.AuthLink onClick={onRegister}>{chunks}</SC.AuthLink>
									}}
								/>
							}
						/>
					)}
					<HookFormField
						configData={configData}
						control={control}
						name={'phone'}
						component={PhoneFormField}
						required
						getPopupContainer={() => document.querySelector<HTMLDivElement>(`#${APP_CONTAINER_ID}`) ?? document.body}
						// NOTE: z-index must be higher than z-index of "ReservationBookingModal" to show the menu over the modal
						// TODO: the solution with z-indexes is not the best, there must be some better solution using getPopupContainer prop, but I can't find one atm due to lack of time
						dropdownStyle={{ zIndex: RESERVATION_BOOKING_MODAL_Z_INDEXES.SELECT_DROPDOWN }}
					/>
					<InfoLine
						type={INFO_LINE_TYPE.INFO}
						message={messages['Please check if the number you have entered is correct. The salon will use it to contact you.']}
					/>
					<HookFormField
						control={control}
						name={'isVisitor'}
						component={CheckboxFormField}
						alignment={'start'}
						checkboxGap={8}
						description={messages['I’m booking for someone else']}
						onAfterChange={(e) => {
							pushToDataLayer(
								getCheckboxClickEvent({
									checked: e.target.checked,
									name: 'reservation_on_behalf',
									type: 'reservation_confirmation'
								})
							)
						}}
					/>
					{isVisitor && (
						<SC.NameFormSection>
							<HookFormField
								control={control}
								name={'visitor.firstName'}
								component={InputFormField}
								label={messages.Name}
								placeholder={messages['Visitor name']}
								maxLength={VALIDATION_MAX_LENGTH.LENGTH_255}
								required
							/>
							<HookFormField
								control={control}
								name={'visitor.lastName'}
								component={InputFormField}
								label={messages.Surname}
								placeholder={messages['Visitor surname']}
								maxLength={VALIDATION_MAX_LENGTH.LENGTH_255}
								required
							/>
						</SC.NameFormSection>
					)}
					<SC.Divider />
					{!isAuthorized && (
						<SC.AllowMarketingCheckboxWrapper>
							<HookFormField
								control={control}
								name={'notAllowMarketing'}
								component={CheckboxFormField}
								alignment={'start'}
								checkboxGap={8}
								description={
									messages['I do not wish to receive information about news, promotions and discount coupons related to this booking']
								}
								onAfterChange={(e) => {
									pushToDataLayer(
										getCheckboxClickEvent({
											checked: e.target.checked,
											name: 'reservation_news_denied',
											type: 'reservation_confirmation'
										})
									)
								}}
							/>
							<Tooltip
								styles={{ root: { maxWidth: 360 } }}
								content={
									<FormattedMessage
										id='Notino s.r.o. is entitled to contact its customers for direct marketing purposes. Please confirm if you object to this processing. For more information, see the <aDataProcessingPolicy>data processing policy.</aDataProcessingPolicy>'
										defaultMessage='Notino s.r.o. is entitled to contact its customers for direct marketing purposes. Please confirm if you object to this processing. For more information, see the <aDataProcessingPolicy>data processing policy.</aDataProcessingPolicy>'
										values={{
											aDataProcessingPolicy: (chunks) => <AgreementLink link={String(privacyPolicyLink)}>{chunks}</AgreementLink>
										}}
									/>
								}
							>
								<IconRegularInfo color={'text.tertiary'} style={{ cursor: 'help' }} />
							</Tooltip>
						</SC.AllowMarketingCheckboxWrapper>
					)}
					<HookFormField
						control={control}
						name={'agreement'}
						component={CheckboxFormField}
						alignment={'start'}
						checkboxGap={8}
						description={
							<SC.AgreementDescription>
								<FormattedMessage
									id='I agree to the <aTermsOfServices>Terms of services reservations</aTermsOfServices> and processing of <aPersonalData>personal data</aPersonalData> for the purpose of creating a reservation.'
									defaultMessage='I agree to the <aTermsOfServices>Terms of services reservations</aTermsOfServices> and processing of <aPersonalData>personal data</aPersonalData> for the purpose of creating a reservation.'
									values={{
										aTermsOfServices: (chunks) => <AgreementLink link={String(reservationTermsLink)}>{chunks}</AgreementLink>,
										aPersonalData: (chunks) => <AgreementLink link={String(privacyPolicyLink)}>{chunks}</AgreementLink>
									}}
								/>
							</SC.AgreementDescription>
						}
						onAfterChange={(e) => {
							pushToDataLayer(
								getCheckboxClickEvent({
									checked: e.target.checked,
									name: 'reservation_terms_accepted',
									type: 'reservation_confirmation'
								})
							)
						}}
					/>
				</SC.FormSectionWrapper>
			</SC.Form>
			<ReservationBookingDialog
				isOpen={isCartExpiredModalOpen}
				onClose={handleCartExpiredConfirm}
				title={messages['Reservation time expired']}
				description={messages['The time to complete your reservation has expired. The selected time slots are no longer reserved for you.']}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleCartExpiredConfirm}>
						{messages.OK}
					</Button>
				}
			/>
			<ReservationBookingDialog
				isOpen={isUnavailableServicesModalOpen && !isCartExpiredModalOpen}
				onClose={handleUnavailableServicesModalConfirm}
				title={formatMessage({ id: 'Service has been booked!', defaultMessage: 'Service has been booked!' })}
				description={unavailableServicesModalDescription}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleUnavailableServicesModalConfirm}>
						{messages.OK}
					</Button>
				}
			/>
		</ReservationBookingModalLayout>
	)
}

export default Step3BookingConfirmation
